import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, createSlots as _createSlots, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "checkbox-row" }

import UnderlineTabGroup from '@/design-system/molecules/UnderlineTabGroup.vue'
import { computed, inject, onUnmounted, ref, toRefs, watch } from 'vue'
import CardInteractive from './CardInteractive.vue'
import { Chart, ChartComponent } from './ChartComponent'
import ChartBase from './ChartComponent/ChartBase.vue'
import LegendBase from './LegendBase/LegendBase.vue'
import { DataSource, useDataSources } from '@/composables/useDataSources'
import { useI18n } from 'vue-i18n'
import {
  compileChart,
  createChartLegendComponents,
  filterChartComponents
} from './ChartComponent/chart/chart'
import { FeatureType, useCurrentFeatureFilters, useFilters } from '@/composables/useFilters'
import { BucketSize } from '@/api-client/models/indoorclimate/Contracts'
import { calculateBucketSizes } from '@/utils/calculateBucketSizes'
import { sharedDataKey } from '../../types'

export type Props = {
  title: string
  info?: string
  enableFullscreen?: boolean
  chart: Chart
  chartHeight?: string
  cardHeight?: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CardChart',
  props: {
    title: {},
    info: { default: undefined },
    enableFullscreen: { type: Boolean },
    chart: {},
    chartHeight: { default: '100%' },
    cardHeight: { default: '400px' }
  },
  setup(__props: any) {

const props = __props
const sharedData = inject(sharedDataKey)
const dashboardFullscreen = computed(() => sharedData?.fullscreen ?? false)
const { chart, chartHeight } = toRefs(props)

const { locale } = useI18n()

const { dataSources } = toRefs(useDataSources())

const showingInfo = ref(false)

const currentFeature = useCurrentFeatureFilters()
const enableResolution = computed(
  () =>
    chart.value.type === 'timeseries' &&
    currentFeature.value?.$id !== 'building_score' &&
    currentFeature.value?.$id !== 'utilization' &&
    currentFeature.value?.$id !== FeatureType.TicketAnalysis
)
const chartBase = ref<InstanceType<typeof ChartBase> | undefined>(undefined)
const viewport = ref<DOMRect | undefined>(undefined)

const components = computed<ChartComponent[] | undefined>(() =>
  viewport.value
    ? compileChart(
        chart.value,
        dataSources.value as Record<string, DataSource<any> | undefined>,
        viewport.value,
        locale.value
      )
    : undefined
)

const filteredComponents = computed<ChartComponent[]>(() =>
  filterChartComponents(chart.value.type, components.value ?? [], enabledLabels.value)
)

const legendComponents = computed(() =>
  createChartLegendComponents(props.chart, locale.value, {
    type: 'tiny'
  })
)
const legendComponentsLarge = computed(() =>
  createChartLegendComponents(props.chart, locale.value, {
    type: 'label'
  })
)

const enabledLabels = ref<Set<string>>(new Set())

const fullscreen = ref(false)

const currentHeight = computed(() => (fullscreen.value ? '600px' : chartHeight.value))

const updateViewport = () => {
  viewport.value = chartBase.value?.$el.getBoundingClientRect()
}

const viewportObserver = new ResizeObserver(updateViewport)

const filters = useFilters(FeatureType.IndoorClimate)
const resolutions: Partial<Record<BucketSize, { en: string; sv: string }>> = {
  MAX: { en: 'Year', sv: 'År' },
  YEAR: { en: 'Year', sv: 'År' },
  QUARTER: { en: 'Quarter', sv: 'Kvartal' },
  MONTH: { en: 'Month', sv: 'Månad' },
  WEEK: { en: 'Week', sv: 'Vecka' },
  DAY: { en: 'Day', sv: 'Dag' },
  HOUR: { en: 'Hour', sv: 'Timme' }
}

const selectedName = computed<string | undefined>(() =>
  filters.current.bucketSize ? resolutions[filters.current.bucketSize]?.[locale.value] : undefined
)
const buttons = ref<
  {
    text: string
    name: string
    onClick: () => void
  }[]
>([])

watch(
  () => filters.current.calendar,
  (value) => {
    if (!value?.from || !value.to) return
    buttons.value = calculateBucketSizes(value.from, value.to)
      .filter((x) => resolutions[x] !== undefined)
      .map((resolution) => {
        return {
          text: resolutions[resolution]![locale.value],
          name: resolutions[resolution]![locale.value],
          onClick: () => {
            filters.current.bucketSize = resolution
            filters.submit()
          }
        }
      })
  },
  { immediate: true }
)

onUnmounted(() => {
  viewportObserver.disconnect()
  filters.submit()
})

watch(
  chartBase,
  () => {
    viewportObserver.disconnect()
    if (chartBase.value) {
      updateViewport()
      viewportObserver.observe(chartBase.value.$el)
    }
  },
  { immediate: true }
)

watch(
  legendComponents,
  () => {
    enabledLabels.value = new Set(legendComponents.value?.map((c) => c.label) ?? [])
  },
  { immediate: true }
)

function onChecked(label: string, checked: boolean) {
  if (checked) {
    enabledLabels.value.add(label)
  } else {
    enabledLabels.value.delete(label)
  }
}

function toggleFullscreen(value: boolean) {
  fullscreen.value = value
  if (!value) {
    filters.resetBucketSize()
    filters.submit()
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(['card-chart', { fullscreen: dashboardFullscreen.value }]),
    style: _normalizeStyle({ height: _ctx.cardHeight })
  }, [
    _createVNode(CardInteractive, {
      title: _ctx.title,
      info: _ctx.info,
      enableFullscreen: _ctx.enableFullscreen,
      showControls: fullscreen.value && enableResolution.value,
      isLoading: !components.value,
      headerDivider: "",
      onToggleFullscreen: toggleFullscreen,
      onToggleShowInfo: _cache[0] || (_cache[0] = (value) => (showingInfo.value = value))
    }, _createSlots({
      controls: _withCtx(() => [
        _createVNode(UnderlineTabGroup, {
          tabStyle: "'pillTabSmall'",
          size: 'small',
          buttons: buttons.value,
          selectedName: selectedName.value
        }, null, 8, ["buttons", "selectedName"])
      ]),
      default: _withCtx(() => [
        _createVNode(ChartBase, {
          ref_key: "chartBase",
          ref: chartBase,
          height: currentHeight.value,
          components: filteredComponents.value
        }, null, 8, ["height", "components"])
      ]),
      _: 2
    }, [
      (legendComponents.value.length > 1)
        ? {
            name: "header",
            fn: _withCtx(() => [
              _createElementVNode("div", _hoisted_1, [
                (fullscreen.value)
                  ? (_openBlock(), _createBlock(LegendBase, {
                      key: 0,
                      components: legendComponentsLarge.value,
                      onChange: onChecked
                    }, null, 8, ["components"]))
                  : (_openBlock(), _createBlock(LegendBase, {
                      key: 1,
                      components: legendComponents.value,
                      onChange: onChecked
                    }, null, 8, ["components"]))
              ])
            ]),
            key: "0"
          }
        : undefined
    ]), 1032, ["title", "info", "enableFullscreen", "showControls", "isLoading"])
  ], 6))
}
}

})